import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit
} from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { MatToolbar } from "@angular/material/toolbar";
import { MatIconModule } from "@angular/material/icon";
import { MatSelectModule } from "@angular/material/select";
import { Footer } from "../footer/footer";
import { TextInputComponent } from "../common/components/general/text-input/text-input";
import { Button } from "../common/components/general/button/button";
import { Icon } from "../common/components/general/icon/icon";
import { SearchSelect } from "../common/components/general/search-select/search-select";
import { MatListItem, MatNavList, MatDivider } from "@angular/material/list";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { eGigyaScreen } from "../common/constants/gigya-screen-sets";
import { LoginService } from "./login-service";
import { Components, MaintenanceData } from "./login-model";

import { Language } from "../common/models/language";
import { eGigyaScript } from "../common/constants/gigya-script-types";
import { ElementsService } from "../common/services/elements-service";
import { ReusableService } from "../common/services/reusable-services";
import { ContentFilterPipe } from "../common/pipes/content-filter-pipe";
import { COMPONENT_META_DATA } from "../common/constants/component-meta-data";
import { CacheService } from "../common/services/cache-service";
import { CACHE_KEYS } from "../common/constants/cache-keys";
import { addFragmentAndReload, areDeepEqual, convertToLocalTimezone, removeQueryParamsFromCurrentUrl } from "../common/utils/utils";
import { MiniCartService } from "../header/mini-cart/mini-cart.service";
import { ApiResponse } from "../common/models/api-response.model";
import { SRP_ERRORS } from "../common/constants/srp-errors";
import { SRP_ROUTES } from "../common/constants/routes";
import { environment } from "../../environments/environment";
import { Subscription, filter } from "rxjs";
import { GigyaService2 } from "../common/services/gigya-service2";

@Component({
  selector: "srp-login",
  imports: [
    Footer,
    MatToolbar,
    MatIconModule,
    MatSelectModule,
    TextInputComponent,
    ReactiveFormsModule,
    Button,
    Icon,
    SearchSelect,
    MatListItem,
    MatNavList,
    MatDivider,
    MatProgressSpinner,
    ContentFilterPipe
  ],
  providers: [ContentFilterPipe],
  templateUrl: "./login.html",
  styleUrl: "./login.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Login implements OnInit, OnDestroy {
  private readonly maintenanceOverrideKey = CACHE_KEYS.LOGIN.MAINTENANCE_OVERRIDE;
  private readonly maintenanceApiEnabledKey = CACHE_KEYS.LOGIN.MAINTENANCE_API_ENABLED;

  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  elementsService = inject(ElementsService);
  gigyaService2 = inject(GigyaService2);
  loginService = inject(LoginService);
  formBuilder = inject(FormBuilder);
  reusableService = inject(ReusableService);
  elementRef = inject(ElementRef);
  cacheService = inject(CacheService);
  miniCartService = inject(MiniCartService);

  private redirectUrl = "n";
  private afterAuth = false;
  private mode = "";
  private samlMode = false;
  private isIR = true;
  private resetPassword = false;
  private resetPasswordUserName = "";
  private readonly pagePath = '/content/gdrp/global/en/login';
 
  userLoginForm: FormGroup = null;
  languageForm: FormGroup = null;
  LABELS = COMPONENT_META_DATA.LOGIN.LABELS;

  showLinkListSignal = signal<boolean>(false);

  loading = signal(false);
  loadGigya = signal(false);
  showForgetPasswordLink = signal(false);
  loginErrorMessage = signal<string>('');

  maintenanceMode = signal(false);
  maintenanceTitle = signal('');
  maintenanceMessage = signal('');

  loginPageState = signal({
    defaultLang: '',
    onBoardDocumentLinks: [],
    reachLink: null,
    languages: [],
    loginFields: null,
    pageContent: [],
    cqPath: null
  });

  contentFilterPipe = inject(ContentFilterPipe);
  private loginRouteSubscription: Subscription;


  // When updating:

  @HostListener('document: click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    const clickedInside =
      target.closest('list-item-container') ||
      target.closest('srp-button')

    if (!clickedInside) {
      this.showLinkListSignal.set(false)
    }
  }

  constructor() {
    this.elementsService.setAngularContext(this.elementRef);
    this.redirectUrl =
      this.activatedRoute.snapshot.queryParamMap.get("redirectUrl");
    this.afterAuth =
      !!this.activatedRoute.snapshot.queryParamMap.get("afterAuth");
    this.resetPassword =
      this.activatedRoute.snapshot.queryParamMap.has("resetpassword");

    this.mode = this.activatedRoute.snapshot.queryParamMap.get("mode");
    this.samlMode = this.activatedRoute.snapshot.queryParamMap.get("saml") === "y";

    this.resetPasswordUserName =
      this.activatedRoute.snapshot.queryParamMap.get("userName") ||
      this.activatedRoute.snapshot.queryParamMap.get("username") ||
      "";


    const pageData = this.activatedRoute.snapshot.data['pageData'];

    if (pageData.pageJSON.data && pageData.languages) {
      const loginPageJSONResponse = pageData.pageJSON as ApiResponse<Components>;
      const loginPageJsonResult = loginPageJSONResponse.data;

      const languagesResult = pageData.languages;
      languagesResult.map((lang: Language) => {
        lang.image = `images/flags/${(lang.countryCode).toLowerCase()}.svg`
      });
      const applicableLanguage = this.reusableService.getApplicableLanguage();
      //this.defaultLang = applicableLanguage?.languageLabel;

      // Initialize language form with default value
      this.languageForm = this.formBuilder.group({
        language: new FormControl(applicableLanguage?.isoCode)
      });

      // Subscribe to language form value changes
      this.languageForm.get('language')?.valueChanges.subscribe(val => {
        if (val) {
          this.onLanguageChange(val);
        }
      });

      this.userLoginForm = this.formBuilder.group({
        userName: new FormControl('', [
          Validators.required,
          Validators.maxLength(15),
        ])
      });

      this.maintenanceMode.set(false);

      this.loginPageState.set({
        defaultLang: applicableLanguage?.languageLabel,
        onBoardDocumentLinks: loginPageJsonResult.pageComponents[0].onBoardDocumentLinks,
        reachLink: loginPageJsonResult.pageComponents[0].reachLinks[0],
        //loginPageJsonResult.pageComponents[0].reachLinks[0].label
        languages: languagesResult,
        loginFields: loginPageJsonResult.pageComponents[0].loginFields,
        pageContent: loginPageJsonResult.pageComponents[0].labels,
        cqPath: loginPageJsonResult.pageComponents[0].cqPath
      });
    }

    this.showLoginError();
  }

   ngOnInit(): void {
    this.checkMaintenanceMode();

    this.loginRouteSubscription = this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationEnd &&
            event.urlAfterRedirects === `/${SRP_ROUTES.LOGIN.PATH}`
        )
      )
      .subscribe(() => {
        this.showLoginError();
      });
  }

  ngOnDestroy(): void {
    this.loginRouteSubscription?.unsubscribe();
  }


  onClick() {
    if (this.userLoginForm.invalid) {
      this.userLoginForm.markAllAsTouched();
      return;
    }
    this.loading.set(true);
    this.initializeGigyaLogin();
  }

  onForgotPasswordLinkClick(userNameOverride?: string) {
    const userName =
      userNameOverride ||
      this.userLoginForm?.get("userName")?.value ||
      "";

    const applicableLanguage =this.reusableService.getApplicableLanguage();

    this.gigyaService2.loadGigyaScripts(eGigyaScript.MAIN).then(() => {
      this.gigyaService2.initGigyaScreen(
        userName,
        "gigyaContainer",
        eGigyaScreen.RESET_PASSWORD,
        applicableLanguage,
        this.afterGigyaScreenLoad
      );
    }, (e) => {
      console.error("Error loading Gigya scripts:", e);
      if (!this.loginErrorMessage()) {
        this.loginErrorMessage.set(this.contentFilterPipe.transform(
          this.loginPageState().pageContent,
          this.LABELS.GIGYA_LOAD_ERROR
        ) || "There was an issue loading the login service. Please try again later.");
      }
    });
  }

  onDocumentLinkClick(event: any, link: string, type: string, path: string, linkType: string) {
    const currentUrl = window.location.origin;
    const title = 'Label';

    if (type.toLowerCase() === 'weblink') {
      this.reusableService.navigateToLink(
        event,
        currentUrl,
        linkType === 'newTab' ? '_blank' : '',
        link,
        this.router,
        title
      );
    } else {
      this.reusableService.navigateToLink(
        event,
        currentUrl,
        path,
        '_blank',
        this.router,
      );
    }

  }


  onReachLinkClick(event: any, title?: string) {
    const sameAppUrl = window.location.origin;
    const link = this.loginPageState().reachLink;
    this.reusableService.navigateToLink(event, sameAppUrl, "_blank", link.type === 'webLink' ? link.link : link.path, this.router, title);
  }

  onLanguageChange(val: string) {
    const supportedLanguages = this.loginPageState().languages;
    const selectedLanguage = supportedLanguages.find(sl => sl.isoCode == val);
    const currentLanguage = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LANGUAGE);
    if (!areDeepEqual(selectedLanguage, currentLanguage)) {
      this.cacheService.addToCache(CACHE_KEYS.LOGIN.LANGUAGE, selectedLanguage);
      addFragmentAndReload();
    }
  }

  toggleList() {
    this.showLinkListSignal.set(!this.showLinkListSignal())
  }


  public initializeGigyaLogin = (userName?: string): void => {
    this.gigyaService2.loadGigyaScripts(eGigyaScript.MAIN).then(() => {
      this.gigyaService2.initGigyaScreen(
        userName ?? "",
        "gigyaContainer",
        eGigyaScreen.LOGIN,
        this.reusableService.getApplicableLanguage(),
        this.afterGigyaScreenLoad,
        this.onSuccessGigyaLogin,
        this.onGigyaError
      );
    }, (e) => {
      console.error("Error loading Gigya scripts:", e);
      if (!this.loginErrorMessage()) {

        this.loginErrorMessage.set(this.contentFilterPipe.transform(
          this.loginPageState().pageContent,
          this.LABELS.GIGYA_LOAD_ERROR
        ) || "There was an issue loading the login service. Please try again later.");
      }
    });
  };

  private onSuccessGigyaLogin = (nickName: string, gigyaUid: string): void => { 
    this.cacheService.addToCache(CACHE_KEYS.USER.GIGYA_UID, gigyaUid);
    this.cacheService.addToCache(CACHE_KEYS.USER.USER_NICKNAME, nickName);
    this.loginService.continueLogin("GIGYA");
  }

  public onGigyaError = (event: any): void => {
    console.error("OnLoginFailed fired: Gigya Login Failed", event);
    this.loginErrorMessage.set('');
  }

  public afterGigyaScreenLoad = (screenType: eGigyaScreen): void => {
    if (this.resetPassword || this.resetPasswordUserName) {
      this.cacheService.addToCache(CACHE_KEYS.LOGIN.FORCE_LOGIN, true);
      removeQueryParamsFromCurrentUrl(['resetpassword', 'userName', 'username']);
    }
    this.loadGigya.set(true);
    if (screenType == eGigyaScreen.LOGIN) {
      this.showForgetPasswordLink.set(false);
    } else {
      //screenType = eGigyaScreen.RESET_PASSWORD
      this.showForgetPasswordLink.set(true);
    }
    this.loading.set(false);
  };

  private showLoginError() {
    let loginError: string = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_ERROR);
    this.cacheService.removeFromCache(CACHE_KEYS.LOGIN.LOGIN_ERROR);

    if (loginError) {
      switch (loginError) {
        case SRP_ERRORS.LOGIN_ERRORS.GRAPHQL_FAILED:
          loginError = this.contentFilterPipe.transform(
            this.loginPageState().pageContent,
            this.LABELS.GRAPHQL_FAILED_ERROR
          ) || 'There was an issue fetching user details. Please try again later.';
          this.loginErrorMessage.set(loginError);
          break;
        case SRP_ERRORS.LOGIN_ERRORS.PAGEJSON_FAILED:
          loginError = this.contentFilterPipe.transform(
            this.loginPageState().pageContent,
            this.LABELS.PAGEJSON_FAILED_ERROR
          ) || 'Unable to fetch content. Please contact Administrator.';
          this.loginErrorMessage.set(loginError);
          break;
        case CACHE_KEYS.LOGIN.POI_VALIDATION_PENDING:
          loginError = this.contentFilterPipe.transform(
            this.loginPageState().pageContent,
            this.LABELS.POI_VALIDATION_PENDING_ERROR
          ) || 'Your username validation is pending. Please try again later.';
          this.loginErrorMessage.set(loginError);
          break;
        case CACHE_KEYS.LOGIN.POI_VALIDATION_REJECTED:
          loginError = this.contentFilterPipe.transform(
            this.loginPageState().pageContent, 
            this.LABELS.POI_VALIDATION_REJECTED_ERROR
          ) || 'Your username validation was rejected. Please contact support.';
          this.loginErrorMessage.set(loginError);
          break;
        default:
          this.loginErrorMessage.set(
            this.contentFilterPipe.transform(
              this.loginPageState().pageContent,
              this.LABELS.LOGIN_DEFAULT_ERROR
            ) || 'There was an issue logging in. Please try again later.');
      }
      
    }
  }

  public triggerLogin() {
    // call the serviceProvider Login API
  }

  onRegisterClick() {
    window.open(environment.REG_URL, '_blank');
  }

  private checkMaintenanceMode(): void {
    const isLocalMaintenanceEnabled = this.isMaintenanceOverrideEnabled();

    this.loginService.getMaintenanceMessage()
     .then((response) => {
      const maintenanceData = response?.data;
      const isApiMaintenanceEnabled = maintenanceData?.enabled === true;

      this.setMaintenanceApiCache(isApiMaintenanceEnabled);

      if (isApiMaintenanceEnabled || isLocalMaintenanceEnabled) {
        this.maintenanceMode.set(true);
        this.maintenanceTitle.set(maintenanceData?.title || 'Scheduled Maintenance');
        this.maintenanceMessage.set(this.resolveMaintenanceMessage(maintenanceData));
        return;
      }

      this.maintenanceMode.set(false);
       this.initializeLoginFlow();
     })
      .catch((error) => {
        console.error('Error fetching maintenance message:', error);
        const isCachedMaintenanceEnabled = this.getCachedMaintenanceApiEnabled();

        if (isLocalMaintenanceEnabled || isCachedMaintenanceEnabled) {
          this.maintenanceMode.set(true);
          this.maintenanceTitle.set('Scheduled Maintenance');
          this.maintenanceMessage.set('This site is currently under maintenance and will be back shortly.');
          return;
        }

        this.maintenanceMode.set(false);
        this.initializeLoginFlow();
      });
  }

  // {0} is replaced with endTime, {1} is replaced with startTime
  private resolveMaintenanceMessage(maintenanceData?: MaintenanceData): string {
    const defaultMessage = 'This site is currently under maintenance and will be back shortly.';
    if (!maintenanceData?.messages) {
      return defaultMessage;
    }

    const endTime = maintenanceData.endTime ? convertToLocalTimezone(maintenanceData.endTime).localDateTime : '';
    const startTime = maintenanceData.startTime ? convertToLocalTimezone(maintenanceData.startTime).localDateTime : '';

    return maintenanceData.messages.replace(/\{0\}/g, endTime).replace(/\{1\}/g, startTime);
  }

  private isMaintenanceOverrideEnabled(): boolean {
    try {
      const value = localStorage.getItem(this.maintenanceOverrideKey);
      if (!value) {
        return false;
      }

      const normalizedValue = value.trim().toLowerCase();
      return ['true', '1', 'yes', 'on'].includes(normalizedValue);
    } catch {
      return false;
    }
  }

  private setMaintenanceApiCache(isEnabled: boolean): void {
    try {
      localStorage.setItem(this.maintenanceApiEnabledKey, String(isEnabled));
    } catch {
      // no-op: local storage unavailable
    }
  }

  private getCachedMaintenanceApiEnabled(): boolean {
    try {
      return localStorage.getItem(this.maintenanceApiEnabledKey) === 'true';
    } catch {
      return false;
    }
  }

  private initializeLoginFlow(): void {

    if (this.resetPassword) {
      this.loadGigya.set(true);
      this.onForgotPasswordLinkClick(this.resetPasswordUserName);
    } else if (this.afterAuth || this.isIR) {
      this.loadGigya.set(true);
      this.initializeGigyaLogin();
    }
  }
}